import type { ClickReleaseConfig } from '../config/types';
import type { ReleaseEntry } from '../content/types';
import { el } from '../util/dom';
import { setSafeHtml } from '../util/html';
import { formatDate } from '../util/date';

/** Fetches the full body of an entry by id — supplied by the SDK, backed by the detail endpoint. */
export type LoadFullEntry = (id: string) => Promise<ReleaseEntry>;

export interface ChangelogBodyOptions {
  /** Static hosted changelog page; rendered as the final CTA when present. */
  staticPageUrl?: string;
}

/** Human labels for the known backend change types; unknown values render their raw string. */
const TYPE_LABELS: Record<string, string> = {
  feature: 'Feature',
  improvement: 'Improvement',
  fix: 'Fix',
};

/** A color-coded pill naming an entry's change type (feature / improvement / fix). */
function renderTypeBadge(category: string): HTMLElement {
  const key = category.trim().toLowerCase();
  const label = TYPE_LABELS[key];
  const cls = label ? `cr-type cr-type--${key}` : 'cr-type';
  return el('span', { class: cls }, [label ?? category]);
}

/** Build the changelog list — shared verbatim by the modal and sidebar surfaces. */
export function renderChangelogBody(
  entries: ReleaseEntry[],
  config: ClickReleaseConfig,
  loadFull?: LoadFullEntry,
  options: ChangelogBodyOptions = {}
): HTMLElement {
  const list = el('div', { class: 'cr-changelog' });

  if (entries.length === 0) {
    list.append(el('div', { class: 'cr-empty' }, ['No updates yet.']));
  } else {
    for (const entry of entries) {
      const item = el('article', { class: 'cr-entry' });

      const head = el('header', { class: 'cr-entry__head' });
      head.append(el('h3', { class: 'cr-entry__title' }, [entry.title]));
      head.append(el('time', { class: 'cr-entry__date', datetime: entry.publishedAt }, [formatDate(entry.publishedAt, config.locale)]));
      item.append(head);

      if (entry.category) {
        item.append(el('div', { class: 'cr-entry__type' }, [renderTypeBadge(entry.category)]));
      }

      if (entry.tags && entry.tags.length > 0) {
        const tags = el('div', { class: 'cr-entry__tags' });
        for (const tag of entry.tags) tags.append(el('span', { class: 'cr-tag' }, [tag]));
        item.append(tags);
      }

      const body = el('div', { class: 'cr-entry__body' });
      setSafeHtml(body, entry.html);
      item.append(body);

      // A summarized entry shows plain text; "Show more" swaps in the full HTML from the detail endpoint.
      if (entry.truncated && loadFull) {
        item.append(buildShowMore(entry.id, body, loadFull));
      }

      list.append(item);
    }
  }

  if (options.staticPageUrl) {
    list.append(buildStaticPageLink(options.staticPageUrl));
  }

  return list;
}

function buildStaticPageLink(url: string): HTMLElement {
  const footer = el('div', { class: 'cr-changelog__footer' });
  footer.append(
    el(
      'a',
      {
        class: 'cr-changelog__more-link',
        href: url,
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      ['View more or Subscribe']
    )
  );
  return footer;
}

/** A "Show more" button that lazily loads and injects an entry's full body, in place. */
function buildShowMore(id: string, body: HTMLElement, loadFull: LoadFullEntry): HTMLElement {
  const btn = el('button', { class: 'cr-entry__more', type: 'button' }, ['Show more']);
  const fail = el('span', { class: 'cr-entry__more-error', role: 'alert' }, ["Couldn't load — try again"]);

  btn.addEventListener('click', () => {
    fail.remove();
    btn.disabled = true;
    btn.textContent = 'Loading…';
    loadFull(id).then(
      full => {
        setSafeHtml(body, full.html);
        btn.remove();
      },
      () => {
        btn.disabled = false;
        btn.textContent = 'Show more';
        btn.after(fail);
      }
    );
  });

  return btn;
}
